import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Col, Row, InputGroup } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

const FinancialInformationConfigurationModal = ({
  initialIbanDetails,
  initialGaccountDetails,
  initialKvkDetails,
  initialBtwDetails,
  initialAccountDetails, 
  initialIfscDetails,
  initialGstDetails, 
  finLabels,
  onSave,
  onClose,
  show,
}) => {
  const { t } = useTranslation();
  const isIndianDomain = finLabels?.showIfsc === true;

  const schema = isIndianDomain
    ? yup.object({
        tempAccountDetails: yup.string().required(t("fieldRequiredText")),
        tempIfscDetails: yup.string().required(t("fieldRequiredText")),
        tempGstDetails: yup.string().required(t("fieldRequiredText")),
      })
    : yup.object({
        tempIbanDetails: yup.string().required(t("ibanDetailsValidationText")),
        tempGaccountDetails: finLabels?.showGAccount
          ? yup.string().required(t("gaccountDetailsValidationText"))
          : yup.string().notRequired(),
        tempKvkDetails: finLabels?.showKvk
          ? yup.string().required(t("kvkDetailsValidationText"))
          : yup.string().notRequired(),
        tempBtwDetails: yup.string().required(t("btwDetailsValidationText")),
      });
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    tempBtwDetails: false,
  });

  useEffect(() => {
    if (show) {
      reset(
        isIndianDomain
          ? {
              tempAccountDetails: initialAccountDetails,
              tempIfscDetails: initialIfscDetails,
              tempGstDetails: initialGstDetails,
            }
          : {
              tempIbanDetails: initialIbanDetails,
              tempGaccountDetails: initialGaccountDetails,
              tempKvkDetails: initialKvkDetails,
              tempBtwDetails: initialBtwDetails,
            }
      );
      setPasswordVisibility({ tempBtwDetails: false });
    }
  }, [show, initialAccountDetails, initialIfscDetails, initialGstDetails, initialIbanDetails, initialGaccountDetails, initialKvkDetails, initialBtwDetails, reset]);

  const handleSave = (data) => {
    onSave(data);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{t("financialInformationConfigurationModalHeaderText")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleSave)}>
          <Row>
            {isIndianDomain ? (
              <>
                {/* IN Domain */}
                <Col md="12" className="mb-3">
                  <Form.Label>{t(finLabels.account)}<span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      {...register("tempAccountDetails")}
                      isInvalid={!!errors.tempAccountDetails}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.tempAccountDetails?.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Col>

                <Col md="12" className="mb-3">
                  <Form.Label>{t(finLabels.ifsc)}<span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      {...register("tempIfscDetails")}
                      isInvalid={!!errors.tempIfscDetails}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.tempIfscDetails?.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Col>

                <Col md="12" className="mb-3">
                  <Form.Label>{t(finLabels.gst)}<span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      {...register("tempGstDetails")}
                      isInvalid={!!errors.tempGstDetails}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.tempGstDetails?.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Col>
              </>
            ) : (
              <>
                {/* NL Domain */}
                <Col md="12" className="mb-3">
                  <Form.Label>{t(finLabels.iban)}<span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      {...register("tempIbanDetails")}
                      isInvalid={!!errors.tempIbanDetails}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.tempIbanDetails?.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Col>

                {finLabels?.showGAccount && (
                  <Col md="12" className="mb-3">
                    <Form.Label>{t(finLabels.gAccount)}<span className="text-danger">*</span></Form.Label>
                    <InputGroup>
                      <Form.Control
                        {...register("tempGaccountDetails")}
                        isInvalid={!!errors.tempGaccountDetails}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.tempGaccountDetails?.message}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Col>
                )}

                {finLabels?.showKvk && (
                  <Col md="12" className="mb-3">
                    <Form.Label>{t("chamberOfCommerce")}<span className="text-danger">*</span></Form.Label>
                    <InputGroup>
                      <Form.Control
                        {...register("tempKvkDetails")}
                        isInvalid={!!errors.tempKvkDetails}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.tempKvkDetails?.message}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Col>
                )}

                <Col md="12" className="mb-3">
                  <Form.Label>{t(finLabels.vat)}<span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      {...register("tempBtwDetails")}
                      isInvalid={!!errors.tempBtwDetails}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.tempBtwDetails?.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Col>
              </>
            )}
          </Row>

          <Row className="justify-content-end">
            <Col xs="auto">
              <Button variant="secondary" size="sm" className="me-3" onClick={onClose}>{t("cancel")}</Button>
              <Button variant="primary" size="sm" type="submit">{t("save")}</Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default FinancialInformationConfigurationModal;